import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useSearch } from "wouter";
import { api, ApiError } from "../api";
import { useCan } from "../auth/session";
import { Card, EmptyState, Field, InlineAlert, PageHeader, PageShell, SegmentedControl, icons, useToast } from "../company-ui";
import { Tip } from "../help/Tip";
import { setCompanyScope, useCompanies, useCompanyScope } from "../shell/company-scope";
import { dollarsToCents, monthName, pctTenth, qty, rateText, usd, wholeDollars } from "./format";
import "./rates.css";

/**
 * Rates and pools (ALN-33, docs/pages/rates.md), laid out as the mockup's v31 rates view
 * (`30-state-rates-normal-v31.png`): the view switch, Company filter and Fill form on one line, the
 * lock callout, Labor pools and Shared pools grouped by company, Normal capacity, then Add a pool
 * beside the impact table. Edits save on change; every refusal shows the server's message next to
 * the field. Reads need `costs.view`; changes need `rates.change` (owner, controller, winemaker).
 */

export interface CompanyDto { id: string; name: string; isProduction: boolean; floorPct: number }
export interface PoolDto {
  id: string; companyId: string; name: string; kind: "labor" | "overhead"; driver: string; driverLocked: boolean;
  capitalizeSummary: { accounts: number; capitalized: number } | null; isRequired: boolean; note: string | null;
  programsCount: number; inProductionCount: number; normalCapacityQty: number | null; capacityUnit: string; fixedSharePct: number; version: number;
}
interface PoolsDto { lockedThrough: string | null; openPeriod: string; companies: CompanyDto[]; labor: PoolDto[]; shared: PoolDto[] }
interface PeriodDto { key: string; kind: "year" | "quarter"; start: string; locked: "full" | "part" | "none"; now: boolean; appliesFrom?: string }
interface CellDto { poolId: string; period: string; rateCents: number | null; inheritedRateCents: number | null; lockedRateCents?: number | null; version: number }
interface RatesDto { by: "year" | "quarter"; year: number; years: number[]; periods: PeriodDto[]; cells: CellDto[] }
interface CapacityRow {
  poolId: string; companyId: string; name: string; kind: string; capacityUnit: string; activityQty: number | null; normalCapacityQty: number;
  fixedSharePct: number; floorPct: number; utilizationPct: number | null; idleCents: number | null; version: number;
}
interface ImpactRow { id: string; name: string; vintage: number; route: string; agingMonths: number; companyId: string; projectedCases: number; planCostPerCaseCents: number | null }

const SUBTITLE = "Rates can change every year or quarter to match how the winery actually ran. A rate applies from its period until a later one replaces it; an empty cell carries the last rate forward (shown in grey). Every cost uses the rate in force in the month the work happens, so a barrel red that ages across three years picks up three years of rates. Owners, controllers and winemakers change rates.";
const NETWORK = "That change wasn't saved. Check the connection and try again.";
const RETURNED = "Changed after submission. Submit it again for approval.";
/** The mockup's drivers (views-v5 DRIVERS), named as the select shows them. */
const DRIVERS: [string, string][] = [["ton", "ton of fruit"], ["acre_month", "acre per month"], ["tank_day", "tank-day"], ["gallon_month", "gallon per month"], ["barrel_gallon_month", "barrel gallon per month"], ["case", "case"], ["case_month", "case per month"]];
const DRIVER_NAME: Record<string, string> = { ...Object.fromEntries(DRIVERS), gallon: "gallon", fixed: "month (fixed)", labor_hour: "hour" };
const ROUTE: Record<string, string> = { none: "No aging", stainless: "Stainless", barrel: "Barrel" };
const errText = (e: unknown) => (e instanceof ApiError ? e.message : NETWORK);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const periodLabel = (key: string) => (key.includes("-Q") ? `${key.slice(5)} ${key.slice(0, 4)}` : key);
const activityLabel = (unit: string, driver: string | undefined) =>
  unit === "barrels" ? "barrels in use per month" : unit === "gallons" ? "gallons in bulk per month" : unit === "tons" ? "tons crushed per month"
    : unit === "acres" ? "acres farmed per month" : driver === "case" ? "cases bottled per month" : "cases stored per month";

/** The By year / By quarter choice and the quarter view's year, remembered per person in this browser. */
function useView(): [{ by: "year" | "quarter"; year: number | null }, (v: { by: "year" | "quarter"; year: number | null }) => void] {
  const read = () => { try { return JSON.parse(localStorage.getItem("vintner.rates.view") ?? "null") as { by: "year" | "quarter"; year: number | null } | null; } catch { return null; } };
  const [view, setView] = useState(() => read() ?? { by: "year" as const, year: null });
  return [view, v => { setView(v); try { localStorage.setItem("vintner.rates.view", JSON.stringify(v)); } catch { /* private mode */ } }];
}

export function RatesPage() {
  const canSee = useCan("costs.view");
  const canEdit = useCan("rates.change");
  const { companyId, tenantId } = useCompanyScope();
  const [view, setView] = useView();
  const scope = companyId ? `companyId=${companyId}` : "";
  const pools = useQuery({ queryKey: ["pools", companyId], queryFn: () => api<PoolsDto>(`/pools?${scope}`), enabled: canSee });
  const rq = new URLSearchParams({ by: view.by, ...(view.by === "quarter" && view.year ? { year: String(view.year) } : {}), ...(companyId ? { companyId } : {}) }).toString();
  const rates = useQuery({ queryKey: ["pool-rates", rq], queryFn: () => api<RatesDto>(`/pools/rates?${rq}`), enabled: canSee, placeholderData: p => p });
  const capacity = useQuery({ queryKey: ["pool-capacity", companyId], queryFn: () => api<{ period: string; pools: CapacityRow[] }>(`/pools/capacity?${scope}`), enabled: canSee });
  const impact = useQuery({ queryKey: ["rates-impact", companyId], queryFn: () => api<{ programs: ImpactRow[] }>(`/programs?${scope}`), enabled: canSee });
  const focusId = new URLSearchParams(useSearch()).get("pool");

  // a pool found by the header search opens here with its row highlighted (F, Search)
  useEffect(() => {
    if (!focusId || !pools.data || !rates.data) return;
    const row = document.getElementById(`pool-${focusId}`);
    if (!row) return;
    row.scrollIntoView({ block: "center" });
    row.querySelector<HTMLElement>("input, button, [tabindex]")?.focus({ preventScroll: true });
  }, [focusId, pools.data, rates.data]);

  if (!canSee) {
    return (
      <PageShell>
        <PageHeader title="Rates and pools" />
        <InlineAlert variant="info" title="You don't have access to rates and pools. Ask an owner, controller or winemaker." />
      </PageShell>
    );
  }

  const failed = pools.isError || rates.isError || capacity.isError;
  const loading = !failed && (pools.isPending || rates.isPending);
  const data = pools.data;
  const all = data ? [...data.labor, ...data.shared] : [];
  const nowPeriod = rates.data?.periods.find(p => p.now);
  // first use (packet B): a required pool with no rate in force; any other unset pool just shows Not set
  const notSet = nowPeriod && rates.data ? all.filter(p => p.isRequired && rates.data.cells.some(c => c.poolId === p.id && c.period === nowPeriod.key && c.rateCents == null && c.inheritedRateCents == null)) : [];

  return (
    <PageShell>
      <PageHeader title="Rates and pools" subtitle={SUBTITLE} />
      <Toolbar view={view} setView={setView} rates={rates.data} companyId={companyId} tenantId={tenantId} canEdit={canEdit && !!data && all.length > 0} />
      {failed ? (
        <Card>
          <EmptyState icon={icons.warning} title="Rates and pools couldn't load. Try again in a minute; nothing was changed."
            action={<button type="button" className="btn" onClick={() => { void pools.refetch(); void rates.refetch(); void capacity.refetch(); }}>Try again</button>} />
        </Card>
      ) : loading || !data || !rates.data ? (
        <Loading />
      ) : (
        <>
          {data.lockedThrough && (
            <div className="ratelock" role="note">
              {icons.lock}
              <p><b>Closed through {monthName(data.lockedThrough)}.</b> Closed months keep the rates they posted with, so a change here applies from {monthName(data.openPeriod)} on and never rewrites history. To fix a closed month, add a correction in the open close.</p>
            </div>
          )}
          {all.length === 0 ? (
            <Card><EmptyState title="No pools yet. Add a labor pool and a shared pool to start planning." /></Card>
          ) : (
            <>
              {notSet.length > 0 && (
                <InlineAlert variant="info" title="Set a rate for each pool to start planning.">
                  Plans and templates that use a pool with no rate show Not costed yet. Required pools carry wine that isn't in a program; set a rate of 0 if you don't use one.
                </InlineAlert>
              )}
              <PoolGrid kind="labor" data={data} rates={rates.data} canEdit={canEdit} focusId={focusId} />
              <PoolGrid kind="overhead" data={data} rates={rates.data} canEdit={canEdit} focusId={focusId} />
              <CapacityCard data={data} rows={capacity.data?.pools} canEdit={canEdit} />
            </>
          )}
          <div className="ratebottom">
            {canEdit && <AddPool data={data} companyId={companyId} />}
            <ImpactCard rows={impact.data?.programs} loading={impact.isPending} failed={impact.isError} />
          </div>
        </>
      )}
    </PageShell>
  );
}

/* ------------------------------------------------------------------ the line under the header */

function Toolbar({ view, setView, rates, companyId, tenantId, canEdit }: { view: { by: "year" | "quarter"; year: number | null }; setView: (v: { by: "year" | "quarter"; year: number | null }) => void; rates: RatesDto | undefined; companyId: string | null; tenantId: string | undefined; canEdit: boolean }) {
  const companies = useCompanies().data?.companies ?? [];
  const companyFieldId = useId();
  const yearFieldId = useId();
  return (
    <div className="ratetools">
      <span className="ratetools__f">
        <label htmlFor={companyFieldId}>Company</label>
        <select id={companyFieldId} className="select select--sm" value={companyId ?? ""} onChange={e => tenantId && setCompanyScope(tenantId, e.target.value || null)}>
          <option value="">All companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </span>
      <SegmentedControl label="Rates by" value={view.by} options={[{ value: "year", label: "By year" }, { value: "quarter", label: "By quarter" }]} onChange={by => setView({ by, year: view.year })} />
      {view.by === "quarter" && rates && (
        <span className="ratetools__f">
          <label htmlFor={yearFieldId}>Year</label>
          <select id={yearFieldId} className="select select--sm" value={rates.year} onChange={e => setView({ by: "quarter", year: Number(e.target.value) })}>
            {rates.years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </span>
      )}
      {canEdit && rates && <FillForm rates={rates} companyId={companyId} />}
    </div>
  );
}

function FillForm({ rates, companyId }: { rates: RatesDto; companyId: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const open = rates.periods.filter(p => p.locked !== "full");
  const [period, setPeriod] = useState(open[open.length - 1]?.key ?? "");
  const [pct, setPct] = useState("3");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const errId = useId();
  useEffect(() => { if (!open.some(p => p.key === period)) setPeriod(open[open.length - 1]?.key ?? ""); }, [rates.by, rates.year]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open.length) return null;
  const refresh = () => Promise.all(["pool-rates", "rates-impact", "pools"].map(k => qc.invalidateQueries({ queryKey: [k] })));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const r = await api<{ fillId: string | null; filled: number; kept: number; message?: string; closeReturnedToDraft?: boolean }>("/pools/rates/fill", { body: { period, pct: pct.trim() === "" ? null : Number(pct), ...(companyId ? { companyId } : {}) } });
      if (!r.filled) { setError(r.message ?? null); return; }
      const signed = `${Number(pct) >= 0 ? "+" : "−"}${Math.abs(Number(pct))}%`;
      const fillId = r.fillId;
      toast(`Filled ${plural(r.filled, `empty ${periodLabel(period)} rate`, `empty ${periodLabel(period)} rates`)} at ${signed}; kept ${r.kept} you'd already set.`, "default", fillId ? {
        label: "Undo",
        onSelect: () => void api<{ removed: number; closeReturnedToDraft: boolean }>(`/pools/rates/fill/${fillId}/undo`, { body: {} })
          .then(u => { toast(`Removed the ${plural(u.removed, "rate", "rates")} the fill added.`); if (u.closeReturnedToDraft) toast(RETURNED); })
          .catch(err => toast(errText(err), "error"))
          .finally(() => void refresh()),
      } : undefined);
      if (r.closeReturnedToDraft) toast(RETURNED);
      void refresh();
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  return (
    <form className="ratefill" onSubmit={submit} noValidate aria-label="Fill empty cells">
      <label className="ratetools__f">Fill empty cells in<Tip term="Fill empty cells" />
        <select className="select select--sm" value={period} onChange={e => setPeriod(e.target.value)} aria-label="Period to fill">
          {open.map(p => <option key={p.key} value={p.key}>{periodLabel(p.key)}</option>)}
        </select>
      </label>
      <label className="ratetools__f">from the period before at
        <input className="input input--sm num ratefill__pct" inputMode="decimal" value={pct} onChange={e => setPct(e.target.value)} aria-label="Percent change" aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined} />%
      </label>
      <button type="submit" className="btn btn--sm" disabled={busy}>Fill rates</button>
      {error && <small className="field__error ratefill__err" id={errId} role="alert">{error}</small>}
    </form>
  );
}

function Loading() {
  return (
    <div className="stackgap" aria-busy="true">
      <p className="sr" role="status">Loading rates and pools…</p>
      {["Labor pools", "Shared pools"].map(t => (
        <Card key={t} title={t}>
          <div className="tablewrap"><table className="t"><thead><tr><th>Pool</th><th className="r">Rates</th><th className="r">Programs</th></tr></thead>
            <tbody>{[0, 1, 2, 3].map(i => <tr key={i} className="rateskel"><td><i /></td><td><i /></td><td><i /></td></tr>)}</tbody></table></div>
        </Card>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ the grids */

function PoolGrid({ kind, data, rates, canEdit, focusId }: { kind: "labor" | "overhead"; data: PoolsDto; rates: RatesDto; canEdit: boolean; focusId: string | null }) {
  const headingId = useId();
  const list = kind === "labor" ? data.labor : data.shared;
  const shared = kind === "overhead";
  const span = 3 + rates.periods.length + (shared ? 2 : 0);
  const [confirm, setConfirm] = useState<string | null>(null);
  const groups = data.companies.map(c => ({ c, pools: list.filter(p => p.companyId === c.id) })).filter(g => g.pools.length);
  return (
    <Card title={shared ? "Shared pools" : "Labor pools"} headingId={headingId}
      description={shared ? "Facility, equipment and energy, spread across programs by each pool's driver. Whether a pool is capitalized into inventory is your CPA's call." : "Loaded hourly rate: wages, payroll taxes and benefits."}>
      <div className="tablewrap rategrid" role="region" aria-label={shared ? "Shared pools table" : "Labor pools table"} tabIndex={0}>
        <table className="t">
          <thead>
            <tr>
              <th scope="col" className="rategrid__pool">Pool</th>
              {shared && <th scope="col"><span className="rateth">Driver<Tip term="Driver" /></span></th>}
              {shared && <th scope="col"><span className="rateth">Capitalize (book)<Tip term="Capitalize (book)" /></span></th>}
              {rates.periods.map(p => (
                <th key={p.key} scope="col" className="r">{periodLabel(p.key)}{p.now && <span className="ratenow">now</span>}</th>
              ))}
              <th scope="col" className="r">Programs</th>
              <th scope="col"><span className="sr">Delete</span></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={span} className="muted">{shared ? "No shared pools yet." : "No labor pools yet."}</td></tr>}
            {groups.map(({ c, pools }) => (
              <Fragment key={c.id}>
                <tr className="rategroup"><th colSpan={span} scope="colgroup">{c.name}</th></tr>
                {pools.map(p => (
                  <PoolRow key={p.id} pool={p} data={data} rates={rates} canEdit={canEdit} span={span} highlight={p.id === focusId}
                    confirming={confirm === p.id} onConfirm={open => setConfirm(open ? p.id : null)} />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function useRefresh() {
  const qc = useQueryClient();
  return () => Promise.all(["pools", "pool-rates", "pool-capacity", "rates-impact"].map(k => qc.invalidateQueries({ queryKey: [k] })));
}

function PoolRow({ pool, data, rates, canEdit, span, highlight, confirming, onConfirm }: { pool: PoolDto; data: PoolsDto; rates: RatesDto; canEdit: boolean; span: number; highlight: boolean; confirming: boolean; onConfirm: (open: boolean) => void }) {
  const shared = pool.kind === "overhead";
  const delBtn = useRef<HTMLButtonElement>(null);
  return (
    <>
      <tr id={`pool-${pool.id}`} className={highlight ? "is-hl" : undefined}>
        <th scope="row" className="rategrid__pool">
          {canEdit ? <NameInput pool={pool} /> : <span className="ratename">{pool.name}</span>}
          {pool.note && <span className="ratenote">{pool.note}</span>}
        </th>
        {shared && <td><DriverCell pool={pool} canEdit={canEdit} /></td>}
        {shared && <td><Capitalize pool={pool} /></td>}
        {rates.periods.map(p => {
          const cell = rates.cells.find(c => c.poolId === pool.id && c.period === p.key) ?? { poolId: pool.id, period: p.key, rateCents: null, inheritedRateCents: null, version: 0 };
          return <td key={p.key} className="r ratecol"><RateCell pool={pool} period={p} cell={cell} canEdit={canEdit} /></td>;
        })}
        <td className="r tnum">{pool.programsCount}</td>
        <td className="rateact">
          {pool.isRequired ? <span className="ratereq">Required<Tip term="Required" /></span>
            : canEdit ? <button ref={delBtn} type="button" className="iconbtn" aria-expanded={confirming} onClick={() => onConfirm(!confirming)}>{icons.x}<span className="sr">Delete {pool.name}</span></button>
            : null}
        </td>
      </tr>
      {confirming && (
        <tr className="rateconfirm"><td colSpan={span}>
          <DeleteConfirm pool={pool} data={data} onCancel={() => { onConfirm(false); requestAnimationFrame(() => delBtn.current?.focus()); }} onDone={() => onConfirm(false)} />
        </td></tr>
      )}
    </>
  );
}

function NameInput({ pool }: { pool: PoolDto }) {
  const refresh = useRefresh();
  const [value, setValue] = useState(pool.name);
  const [error, setError] = useState<string | null>(null);
  const errId = useId();
  // after an edit conflict the refreshed name replaces what was typed, so a second blur can't overwrite theirs
  const resync = useRef(false);
  useEffect(() => { if (!error || resync.current) { resync.current = false; setValue(pool.name); } }, [pool.name, pool.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = async () => {
    const name = value.trim();
    if (name === pool.name) { setError(null); return; }
    if (!name) { setValue(pool.name); setError("A pool needs a name."); return; }
    try {
      await api(`/pools/${pool.id}`, { method: "PATCH", body: { name, version: pool.version } });
      setError(null);
    } catch (e) {
      setError(errText(e));
      if (e instanceof ApiError && e.code === "edit_conflict") resync.current = true;
    }
    void refresh();
  };
  return (
    <span className="ratefield">
      <input id={`pool-name-${pool.id}`} className="input input--sm ratename-in" value={value} maxLength={80} aria-label={`Name of ${pool.name}`}
        aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined}
        onChange={e => setValue(e.target.value)} onBlur={() => void commit()} onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
      {error && <small className="field__error" id={errId}>{error}</small>}
    </span>
  );
}

function DriverCell({ pool, canEdit }: { pool: PoolDto; canEdit: boolean }) {
  const refresh = useRefresh();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const label = `per ${DRIVER_NAME[pool.driver] ?? pool.driver}`;
  if (pool.driverLocked) {
    return <span className="ratedriver"><span className="ratedriver__t">{icons.lock}{label}</span><small className="muted">Fixed by the close until per-pool drivers (ALN-74).</small></span>;
  }
  if (!canEdit) return <span>{label}</span>;
  const options = DRIVERS.some(([d]) => d === pool.driver) ? DRIVERS : [...DRIVERS, [pool.driver, DRIVER_NAME[pool.driver] ?? pool.driver] as [string, string]];
  const change = async (driver: string) => {
    try {
      await api(`/pools/${pool.id}`, { method: "PATCH", body: { driver, version: pool.version } });
      setError(null);
      toast(`${pool.name} is now planned per ${DRIVER_NAME[driver] ?? driver}. Check its rates; the old ones were per the previous driver.`);
    } catch (e) { setError(errText(e)); }
    void refresh();
  };
  return (
    <span className="ratefield">
      <select className="select select--sm" value={pool.driver} aria-label={`Driver for ${pool.name}`} aria-invalid={error ? true : undefined} onChange={e => void change(e.target.value)}>
        {options.map(([d, n]) => <option key={d} value={d}>per {n}</option>)}
      </select>
      {error && <small className="field__error">{error}</small>}
    </span>
  );
}

function Capitalize({ pool }: { pool: PoolDto }) {
  const s = pool.capitalizeSummary;
  if (!s) return null;
  const text = s.accounts === 0 ? "No accounts mapped yet"
    : s.capitalized === s.accounts ? `Capitalized (book): ${s.capitalized} of ${plural(s.accounts, "account", "accounts")}`
    : s.capitalized === 0 ? `Expensed (book): 0 of ${plural(s.accounts, "account", "accounts")}`
    : `Mixed: ${s.capitalized} of ${s.accounts} accounts capitalized`;
  return <span className="ratecap"><span>{text}</span><Link href="/absorption?step=map">Set on Monthly close › Map</Link></span>;
}

function RateCell({ pool, period, cell, canEdit }: { pool: PoolDto; period: PeriodDto; cell: CellDto; canEdit: boolean }) {
  const toast = useToast();
  const refresh = useRefresh();
  const own = cell.rateCents == null ? "" : rateText(cell.rateCents);
  const [value, setValue] = useState(own);
  const [error, setError] = useState<string | null>(null);
  const reset = useRef(false);
  // the version the person started typing against; a change that lands meanwhile never overwrites what they typed, and the save gets edit_conflict
  const editing = useRef<number | null>(null);
  const errId = useId();
  useEffect(() => { if ((!error && editing.current == null) || reset.current) { setValue(own); reset.current = false; } }, [cell.version, cell.rateCents]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = `${pool.name} rate for ${periodLabel(period.key)}`;

  if (period.locked === "full") {
    const posted = cell.lockedRateCents;
    return (
      <span className="ratelocked" title={`${periodLabel(period.key)} is closed. This is the rate it posted with.`}>
        {icons.lock}<span className="tnum">{posted == null ? "—" : rateText(posted)}</span>
        <span className="sr">{label}: closed{posted == null ? ", no rate" : ""}</span>
      </span>
    );
  }
  const inherited = cell.inheritedRateCents;
  const notSet = cell.rateCents == null && inherited == null;
  const notSetTitle = `No rate yet. Plans and templates that use ${pool.name} show Not costed yet until you set one; 0 counts as a rate.`;
  const partTitle = period.appliesFrom ? `Applies from ${monthName(period.appliesFrom)}; closed months keep the rate they posted with` : undefined;
  if (!canEdit) {
    return cell.rateCents != null ? <span className="tnum" title={partTitle}>{rateText(cell.rateCents)}</span>
      : inherited != null ? <span className="tnum rategrey" title="Carried forward from the period before">{rateText(inherited)}</span>
      : <span className="ratenotset" title={notSetTitle}>Not set</span>;
  }
  const commit = async () => {
    const typed = value.trim();
    const version = editing.current ?? cell.version;
    editing.current = null;
    if (typed === own) { setError(null); return; }
    let rateCents: string | null = null;
    if (typed !== "") {
      rateCents = dollarsToCents(typed);
      if (rateCents == null) { setError("Enter a number of zero or more."); return; }
    } else if (cell.rateCents == null) { setError(null); return; }
    try {
      const r = await api<{ closeReturnedToDraft: boolean }>(`/pools/${pool.id}/rates/${period.key}`, { method: "PUT", body: { rateCents, version } });
      setError(null);
      if (r.closeReturnedToDraft) toast(RETURNED);
    } catch (e) {
      setError(errText(e));
      if (e instanceof ApiError && e.code === "edit_conflict") reset.current = true;
    }
    void refresh();
  };
  return (
    <span className="ratefield ratefield--r">
      <input className="input input--sm num ratein" inputMode="decimal" value={value} aria-label={label}
        placeholder={inherited != null ? rateText(inherited) : "Not set"} title={notSet ? notSetTitle : partTitle}
        aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined}
        onChange={e => { if (editing.current == null) editing.current = cell.version; setValue(e.target.value); }} onBlur={() => void commit()}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
      {error && <small className="field__error" id={errId}>{error}</small>}
    </span>
  );
}

function DeleteConfirm({ pool, data, onCancel, onDone }: { pool: PoolDto; data: PoolsDto; onCancel: () => void; onDone: () => void }) {
  const toast = useToast();
  const refresh = useRefresh();
  const qc = useQueryClient();
  const labor = pool.kind === "labor";
  const targets = labor ? data.labor.filter(p => p.companyId === pool.companyId && p.id !== pool.id) : [];
  const [moveTo, setMoveTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLElement | null>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const n = pool.programsCount;
  const k = pool.inProductionCount;
  const del = useMutation({
    mutationFn: () => api<{ deletedId: string; name: string; replans: string[] }>(`/pools/${pool.id}`, { method: "DELETE", body: { version: pool.version, ...(labor && n > 0 ? { moveHoursTo: moveTo || null } : {}) } }),
    onError: e => setError(errText(e)),
    onSuccess: r => {
      onDone();
      const replans = r.replans.length ? ` ${r.replans.length === 1 ? "1 program in production has" : `${r.replans.length} programs in production have`} it in a re-plan to approve.` : "";
      toast(`Deleted ${r.name}.${replans}`, "default", {
        label: "Undo",
        onSelect: () => void api<{ message: string }>(`/pools/${pool.id}/restore`, { body: {} })
          .then(u => { toast(u.message); return refresh().then(() => document.getElementById(`pool-name-${pool.id}`)?.focus()); })
          .catch(err => { toast(errText(err), "error"); void refresh(); }),
      });
      void refresh().then(() => document.querySelector<HTMLElement>(".rategrid")?.focus());
      void qc.invalidateQueries({ queryKey: ["programs"] });
    },
  });
  const production = k === 0 ? "" : labor
    ? (k === 1 ? " 1 of them is in production, so the move goes into its re-plan for approval." : ` ${k} of them are in production, so the move goes into their re-plans for approval.`)
    : (k === 1 ? " 1 of them is in production, so removing it goes into its re-plan for approval." : ` ${k} of them are in production, so removing it goes into their re-plans for approval.`);
  const busy = del.isPending;
  return (
    <div className="ratedel" role="group" aria-label={`Delete ${pool.name}`}>
      {labor && n > 0 ? (
        <>
          <Field label="Move hours to" className="ratedel__f">
            <select ref={el => { first.current = el; }} className="select select--sm" value={moveTo} onChange={e => setMoveTo(e.target.value)}>
              <option value="">Pick a pool</option>
              {targets.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <p className="ratedel__t">Its hours in {plural(n, "program", "programs")} move to the pool you pick.{production}</p>
          <div className="actions">
            <button type="button" className="btn btn--sm btn--destructive" disabled={busy} onClick={() => del.mutate()}>Move and delete</button>
            <button type="button" className="btn btn--sm" onClick={onCancel}>Cancel</button>
          </div>
        </>
      ) : n > 0 ? (
        <>
          <p className="ratedel__t"><b>Delete {pool.name}?</b> It's used by {plural(n, "program", "programs")}.{production}</p>
          <div className="actions">
            <button ref={el => { first.current = el; }} type="button" className="btn btn--sm btn--destructive" disabled={busy} onClick={() => del.mutate()}>Remove from {plural(n, "program", "programs")} and delete</button>
            <button type="button" className="btn btn--sm" onClick={onCancel}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <p className="ratedel__t"><b>Delete {pool.name}?</b></p>
          <div className="actions">
            <button ref={el => { first.current = el; }} type="button" className="btn btn--sm btn--destructive" disabled={busy} onClick={() => del.mutate()}>Delete</button>
            <button type="button" className="btn btn--sm" onClick={onCancel}>Cancel</button>
          </div>
        </>
      )}
      {error && <p className="field__error ratedel__err" role="alert">{error}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ normal capacity */

function CapacityCard({ data, rows, canEdit }: { data: PoolsDto; rows: CapacityRow[] | undefined; canEdit: boolean }) {
  const headingId = useId();
  const groups = data.companies.map(c => ({ c, rows: (rows ?? []).filter(r => r.companyId === c.id) })).filter(g => g.rows.length);
  const driverOf = new Map([...data.labor, ...data.shared].map(p => [p.id, p]));
  return (
    <Card headingId={headingId} title={<span className="rateth">Normal capacity<Tip term="Normal capacity" /></span>}
      description={`What each pool is sized for in a normal month. Fixed cost absorbs into wine at this level; below the floor, the unused share is expensed as idle capacity (ASC 330-10-30-3). Utilization shown for ${monthName(data.openPeriod)}.`}>
      <div className="tablewrap" role="region" aria-label="Normal capacity table" tabIndex={0}>
        <table className="t ratecap-t">
          <thead>
            <tr>
              <th scope="col">Pool</th><th scope="col">Activity</th><th scope="col" className="r">This month</th>
              <th scope="col" className="r"><span className="rateth">Normal capacity<Tip term="Normal capacity" /></span></th>
              <th scope="col" className="r"><span className="rateth">Fixed share<Tip term="Fixed share" /></span></th>
              <th scope="col" className="r"><span className="rateth">Used<Tip term="Used" /></span></th>
              <th scope="col" className="r"><span className="rateth">Idle, expensed<Tip term="Idle, expensed" /></span></th>
            </tr>
          </thead>
          <tbody>
            {rows && groups.length === 0 && <tr><td colSpan={7} className="muted">No pool here has a normal capacity yet.</td></tr>}
            {!rows && <tr className="rateskel"><td colSpan={7}><i /></td></tr>}
            {groups.map(({ c, rows: list }) => (
              <Fragment key={c.id}>
                <tr className="rategroup">
                  <th colSpan={7} scope="colgroup">
                    <span className="rategroup__n">{c.name}</span>
                    <span className="rategroup__floor">Expense idle cost below {pctTenth(c.floorPct)} of normal.<Tip term="Expense idle cost below" /> Change it in <Link href={`/compliance?tab=policies&company=${c.id}`}>Compliance › Policies</Link>.</span>
                  </th>
                </tr>
                {list.map(r => <CapacityLine key={r.poolId} row={r} driver={driverOf.get(r.poolId)?.driver} canEdit={canEdit} />)}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CapacityLine({ row, driver, canEdit }: { row: CapacityRow; driver: string | undefined; canEdit: boolean }) {
  const below = row.utilizationPct != null && row.utilizationPct < row.floorPct;
  return (
    <tr>
      <th scope="row" className="ratecap-n">{row.name}</th>
      <td className="muted small">{activityLabel(row.capacityUnit, driver)}</td>
      <td className="r tnum">{row.activityQty == null ? "—" : qty(row.activityQty)}</td>
      <td className="r">{canEdit ? <CapacityInput row={row} field="normalCapacityQty" label={`Normal capacity for ${row.name}`} /> : <span className="tnum">{qty(row.normalCapacityQty)}</span>}</td>
      <td className="r">{canEdit ? <CapacityInput row={row} field="fixedSharePct" label={`Fixed share for ${row.name}`} suffix="%" /> : <span className="tnum">{pctTenth(row.fixedSharePct)}</span>}</td>
      <td className={`r tnum${below ? " ratewarn" : ""}`}>{row.utilizationPct == null ? "—" : pctTenth(row.utilizationPct)}</td>
      <td className="r tnum">{row.idleCents ? wholeDollars(row.idleCents) : <span className="muted" aria-label="None">—</span>}</td>
    </tr>
  );
}

function CapacityInput({ row, field, label, suffix }: { row: CapacityRow; field: "normalCapacityQty" | "fixedSharePct"; label: string; suffix?: string }) {
  const toast = useToast();
  const refresh = useRefresh();
  const current = String(row[field]);
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const errId = useId();
  // after an edit conflict the refreshed value replaces what was typed, so a second blur can't overwrite theirs
  const resync = useRef(false);
  useEffect(() => { if (!error || resync.current) { resync.current = false; setValue(current); } }, [current, row.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = async () => {
    const typed = value.trim();
    if (typed === current) { setError(null); return; }
    try {
      const r = await api<{ closeReturnedToDraft: boolean }>(`/pools/${row.poolId}/capacity`, { method: "PATCH", body: { [field]: typed, version: row.version } });
      setError(null);
      if (r.closeReturnedToDraft) toast(RETURNED);
    } catch (e) {
      setError(errText(e));
      if (e instanceof ApiError && e.code === "edit_conflict") resync.current = true;
    }
    void refresh();
  };
  return (
    <span className="ratefield ratefield--r">
      <span className="ratecapin">
        <input className="input input--sm num" inputMode="decimal" value={value} aria-label={label} aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined}
          onChange={e => setValue(e.target.value)} onBlur={() => void commit()} onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }} />
        {suffix && <span className="muted" aria-hidden="true">{suffix}</span>}
      </span>
      {error && <small className="field__error" id={errId}>{error}</small>}
    </span>
  );
}

/* ------------------------------------------------------------------ add a pool and the impact table */

function AddPool({ data, companyId }: { data: PoolsDto; companyId: string | null }) {
  const toast = useToast();
  const refresh = useRefresh();
  const headingId = useId();
  const only = data.companies.length === 1 ? data.companies[0]!.id : companyId;
  const [company, setCompany] = useState(only ?? "");
  const [kind, setKind] = useState<"labor" | "overhead">("overhead");
  const [error, setError] = useState<{ field: string | null; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => { if (only) setCompany(only); }, [only]);
  const shared = kind === "overhead";
  const errFor = (f: string) => (error && error.field === f ? error.message : null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const name = String(f.get("name") ?? "").trim();
    const typed = String(f.get("rate") ?? "").trim();
    setBusy(true); setError(null);
    try {
      await api("/pools", { body: {
        companyId: company || null, kind, name, rateCents: typed === "" ? null : dollarsToCents(typed) ?? typed,
        ...(shared ? { driver: f.get("driver"), note: String(f.get("note") ?? "").trim() || null } : {}),
      } });
      toast(shared ? `${name} added. Add it to a stage from the builder's Pools card.` : `${name} added. Pick it in any People table.`);
      form.current?.reset();
      void refresh();
    } catch (err) {
      const field = err instanceof ApiError && typeof err.details.field === "string" ? err.details.field : null;
      setError({ field: field === "companyId" ? "company" : field === "rateCents" ? "rate" : field, message: errText(err) });
    } finally { setBusy(false); }
  };
  return (
    <form ref={form} className="card rateadd" onSubmit={submit} noValidate aria-labelledby={headingId}>
      <div className="card__h"><div>
        <h2 className="card__t" id={headingId}>Add a pool</h2>
        <p className="card__d">A new labor pool shows up in every People table; a new shared pool can be added to any stage.</p>
      </div></div>
      <div className="card__c stackgap">
        <div className="fields fields--2">
          <Field label="Company" error={errFor("company")}>
            <select className="select" value={company} onChange={e => setCompany(e.target.value)}>
              <option value="">Pick a company</option>
              {data.companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Kind">
            <select className="select" value={kind} onChange={e => setKind(e.target.value as "labor" | "overhead")}>
              <option value="labor">Labor pool</option>
              <option value="overhead">Shared pool</option>
            </select>
          </Field>
          <Field label="Name" error={errFor("name")}><input name="name" className="input" placeholder={shared ? "Press and destemmer lease" : "Tasting room staff"} maxLength={80} /></Field>
          {shared && (
            <Field label="Spread by" tip={<Tip term="Driver" />} error={errFor("driver")}>
              <select name="driver" className="select" defaultValue="ton">{DRIVERS.map(([d, n]) => <option key={d} value={d}>per {n}</option>)}</select>
            </Field>
          )}
          <Field label={shared ? "Rate per driver unit ($)" : "Loaded rate per hour ($)"} error={errFor("rate")}>
            <input name="rate" className="input num" inputMode="decimal" />
          </Field>
        </div>
        {shared && <Field label="Note (optional)" error={errFor("note")}><input name="note" className="input" placeholder="What's in this pool and why" maxLength={200} /></Field>}
        {error && !["company", "name", "rate", "driver", "note"].includes(error.field ?? "") && <p className="field__error" role="alert">{error.message}</p>}
        <div className="actions"><button type="submit" className="btn btn--primary" disabled={busy}>{icons.plus}Add pool</button></div>
      </div>
    </form>
  );
}

function ImpactCard({ rows, loading, failed }: { rows: ImpactRow[] | undefined; loading: boolean; failed: boolean }) {
  const headingId = useId();
  let body: ReactNode;
  if (loading) body = <p className="card__c muted" role="status">Loading programs…</p>;
  else if (failed) body = <p className="card__c field__error">Programs couldn't load. Try again in a minute.</p>;
  else if (!rows?.length) body = <div className="card__c"><p className="muted">No programs yet. Plan one in <Link href="/programs">Programs</Link> and its cost per case shows here.</p></div>;
  else body = (
    <div className="tablewrap" role="region" aria-label="Program impact table" tabIndex={0}>
      <table className="t">
        <thead><tr><th scope="col">Program</th><th scope="col">Runs</th><th scope="col" className="r"><span className="rateth">Plan cost / case<Tip term="Plan cost / case" /></span></th><th scope="col" className="r">Plan total</th></tr></thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id}>
              <th scope="row" className="rateimp-n"><Link href={`/builder?program=${p.id}`}>{p.name}</Link></th>
              <td className="small muted">{ROUTE[p.route] ?? p.route}{p.agingMonths ? `, ${p.agingMonths} mo` : ""}, harvest {p.vintage}</td>
              <td className="r tnum">{p.planCostPerCaseCents == null ? <span className="muted">Not costed yet</span> : usd(p.planCostPerCaseCents)}</td>
              <td className="r tnum">{p.planCostPerCaseCents == null ? "—" : wholeDollars(Math.round(p.planCostPerCaseCents * p.projectedCases))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <section className="card rateimpact" aria-labelledby={headingId}>
      <div className="card__h"><div>
        <h2 className="card__t" id={headingId}>What these rates mean for each program</h2>
        <p className="card__d">Plan cost per case as on Programs. Plans not approved yet move with these rates; approved plans keep theirs until a re-plan.</p>
      </div></div>
      {body}
    </section>
  );
}
